import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { ApiResponse } from 'further-types/api';
import { UserType } from 'further-types/user';
import { useCurrentUserInfo } from './useCurrentUserInfo';

type RefetchCurrentUserInfo = ReturnType<typeof useCurrentUserInfo>['refetch'];

type Input = {
  isSubdomain: boolean;
  isLoggedIn: boolean;
  userType: UserType | undefined;
  activeAccountIds?: Array<string>;
  refetchCurrentUserInfo: RefetchCurrentUserInfo;
};

export function useRefreshGreenInvestorAccounts(input: Input): {
  isRefreshing: boolean;
} {
  const activeAccountId = input.activeAccountIds?.[0];

  const shouldRefresh =
    !input.isSubdomain &&
    input.isLoggedIn &&
    input.userType === UserType.Investor &&
    !!activeAccountId &&
    input.activeAccountIds?.length === 1;

  const refreshQuery = useQuery({
    queryKey: ['auth', 'refresh-green-investor-accounts', activeAccountId],
    enabled: shouldRefresh,
    queryFn: async () => {
      await axios.post<ApiResponse<null>>(
        `/further-user/refresh-investor-accounts`,
      );
      await input.refetchCurrentUserInfo();
      return true;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,
    retry: (failureCount: number, error: Error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return false;
      }
      return failureCount < 1;
    },
  });

  return {
    isRefreshing: refreshQuery.isFetching,
  };
}
